import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./context/AuthContext";
import ImageMaskSelector from "./components/ImageMaskSelector";
import { SelectionCoordinates } from "./components/ImageMaskSelector";
import { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import ImageUploader from "./components/ImageUploader";
import RectangleCoordinateInput, {
  RectangleCoordinates,
} from "./components/RectangleCoordinateInput";
import api from "./services/api";
import { uploadAvatarImage, base64ToFile } from "./utils/uploadUtils";
const apiUrl = import.meta.env.VITE_API_URL;
// Define types for our character data
interface CategoryOptions {
  [category: string]: string[];
}

// Define types for dropdown options
interface OptionItem {
  value: string;
  label: string;
}

interface OptionGroup {
  label: string;
  options: OptionItem[];
}

type DropdownOption = OptionItem | OptionGroup;

// Define types for image generation
interface GeneratedImage {
  data: string;
  selected: boolean;
  localPath?: string | null;
}

// Define interface for batch processing
interface BatchPrompt {
  prompt: string;
  images: GeneratedImage[];
  selectedImageIndex: number | null;
}

// Define proper interfaces for detection results based on the backend responses
interface BoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
}

interface DetectionResult {
  id: string;
  class_name: string;
  score: number;
  bbox: BoundingBox;
  num_predicted_pixels?: number;
}

interface ObjectDetectionResponse {
  predictions: DetectionResult[];
  raw_response: any;
  success: boolean;
  error?: string;
  uploaded_image_url?: string;
}

// Define interface to handle uploaded region data
interface RegionData {
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  file?: File;
}

// Create interface for ImageMaskSelector ref
interface ImageMaskSelectorHandle {
  resetComponentState: () => void;
}

// Helper function to organize options into groups
const organizeDropdownOptions = (
  options: any,
  category: string
): DropdownOption[] => {
  if (!options || !options[category]) return [];

  // Special handling for backgrounds to group them by type
  if (category === "background") {
    const cleanOptions: OptionItem[] = [];
    const messyOptions: OptionItem[] = [];
    const otherOptions: OptionItem[] = [];

    options[category].forEach((background: string) => {
      if (
        background.includes("Clean") ||
        background.includes("minimalist") ||
        background.includes("neat")
      ) {
        cleanOptions.push({ value: background, label: background });
      } else if (
        background.includes("Messy") ||
        background.includes("unmade") ||
        background.includes("scattered")
      ) {
        messyOptions.push({ value: background, label: background });
      } else {
        otherOptions.push({ value: background, label: background });
      }
    });

    return [
      { label: "Clean Spaces", options: cleanOptions },
      { label: "Messy Spaces", options: messyOptions },
      { label: "Other Spaces", options: otherOptions },
    ] as OptionGroup[];
  }

  // For other categories, just return flat options
  return options[category].map((item: string) => ({
    value: item,
    label: item,
  })) as OptionItem[];
};

// Helper function to check if an option is a group
const isOptionGroup = (option: DropdownOption): option is OptionGroup => {
  return "options" in option;
};

export default function AddCharacterPage() {
  // Batch processing states
  const [batchPrompts, setBatchPrompts] = useState<string>("");
  const [batchProcessing, setBatchProcessing] = useState<boolean>(false);
  const [batchResults, setBatchResults] = useState<BatchPrompt[]>([]);
  const [batchProgress, setBatchProgress] = useState<number>(0);
  const [detectingObjects, setDetectingObjects] = useState<boolean>(false);
  const [detectionResults, setDetectionResults] = useState<DetectionResult[]>(
    []
  );
  const [detectionProperties, setDetectionProperties] = useState<string[]>([]);

  // Original states
  const [prompt, setPrompt] = useState<string>("");
  const [values, setValues] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [dropdownOptions, setDropdownOptions] = useState<CategoryOptions>({});
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  // Track if user has modified the default values
  const [valuesModified, setValuesModified] = useState<boolean>(false);
  // Keep track of the original values from the last generation
  const originalValuesRef = useRef<{ [key: string]: string }>({});

  // Convert valuesByCategory to a state variable so it persists across renders
  const [valuesByCategory, setValuesByCategory] = useState<{
    [key: string]: string;
  }>({});

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [maskUrl, setMaskUrl] = useState<string | null>(null);

  // Image generation states
  const [generatingImages, setGeneratingImages] = useState<boolean>(false);
  const [generateProgress, setGenerateProgress] = useState<number>(0);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [creditsLeft, setCreditsLeft] = useState<number | null>(null);

  // State to store uploaded region data
  const [uploadedRegions, setUploadedRegions] = useState<RegionData[]>([]);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");

  // Debug image upload states
  const [debugImage, setDebugImage] = useState<File | null>(null);
  const [debugImageUrl, setDebugImageUrl] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Rectangle coordinates for the clickable area
  const [rectCoordinates, setRectCoordinates] = useState<RectangleCoordinates>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  // Add this new state for the selected image URL
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const { token } = useAuth();
  const navigateTo = useNavigate();

  // Group tags by category
  const categoryLabels: { [key: string]: string } = {
    gender: "Gender",
    age: "Age",
    ethnicity: "Ethnicity",
    shot_type: "Shot Type",
    clothing_style: "Clothing & Style",
    hand_pose: "Hand Pose/Objects",
    background: "Background",
  };

  // Update the type of the ref
  const maskSelectorRef = useRef<ImageMaskSelectorHandle>(null);

  // Handler for ImageMaskSelector's regions using useCallback to prevent infinite loop
  const handleRegionsUpdated = useCallback((regions: any[]) => {
    // Only update if the regions are different to prevent unnecessary renders
    const formattedRegions = regions.map((region) => ({
      coordinates: {
        x: region.x,
        y: region.y,
        width: region.width,
        height: region.height,
      },
      file: region.file,
    }));

    // Compare with existing regions and only update if different
    setUploadedRegions(formattedRegions);

    // Don't log on every render to prevent console spam
    if (regions.length > 0) {
      console.log("Updated regions:", formattedRegions);
    }
  }, []); // Empty dependency array means this function never changes

  // Batch processing functions
  const handleBatchPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setBatchPrompts(e.target.value);
  };

  const generateBatchImages = async () => {
    if (!batchPrompts.trim()) {
      setError("Please enter at least one prompt");
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    setError("");

    try {
      // Split prompts by new line
      const prompts = batchPrompts
        .split("\n")
        .filter((prompt) => prompt.trim().length > 0);

      if (prompts.length === 0) {
        throw new Error("No valid prompts found");
      }

      // Initialize batch results
      const initialBatchResults: BatchPrompt[] = prompts.map((prompt) => ({
        prompt,
        images: [],
        selectedImageIndex: null,
      }));

      setBatchResults(initialBatchResults);

      // Set progress to indicate we're starting
      setBatchProgress(10);

      // Create an array of promise-returning functions
      const generationPromises = prompts.map((prompt, index) => {
        return async () => {
          try {
            console.log(
              `Generating images for prompt ${index + 1}/${
                prompts.length
              }: "${prompt.substring(0, 50)}..."`
            );

            const response = await api.post("/api/characters/generate-images", {
              prompt: prompt,
            });

            if (!response.data || !response.data.success) {
              throw new Error(
                `Failed to generate images for prompt ${index + 1}`
              );
            }

            // Extract images and local paths from response
            const localPaths = response.data.local_paths || [];

            // Extract images from response
            const result = {
              prompt,
              images: response.data.images.map(
                (base64: string, idx: number) => ({
                  data: `data:image/png;base64,${base64}`,
                  selected: false,
                  localPath: localPaths[idx] || null,
                })
              ),
              selectedImageIndex: null,
            };

            // Update credits left
            if (response.data.credits_left !== undefined) {
              setCreditsLeft(response.data.credits_left);
            }

            return { index, result };
          } catch (error) {
            console.error(
              `Error generating images for prompt ${index + 1}:`,
              error
            );
            return {
              index,
              error,
              result: { prompt, images: [], selectedImageIndex: null },
            };
          }
        };
      });

      // We'll process promises in batches to avoid overwhelming the API
      const BATCH_SIZE = 3; // Process 3 prompts at a time
      const results: { index: number; result: BatchPrompt; error?: any }[] = [];

      // Process promises in batches
      for (let i = 0; i < generationPromises.length; i += BATCH_SIZE) {
        const batch = generationPromises.slice(i, i + BATCH_SIZE);

        // Update progress
        const percentComplete = Math.floor(
          (i / generationPromises.length) * 90
        );
        setBatchProgress(10 + percentComplete);

        // Run current batch of promises in parallel
        const batchResults = await Promise.all(batch.map((fn) => fn()));
        results.push(...batchResults);

        // Update the batch results after each batch completes
        const updatedResults = [...initialBatchResults];
        results.forEach(({ index, result }) => {
          updatedResults[index] = result;
        });

        // Update state with current progress
        setBatchResults([...updatedResults]);
      }

      // Set progress to 100% when done
      setBatchProgress(100);
    } catch (err) {
      console.error("Error in batch processing:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setBatchProcessing(false);
    }
  };

  const selectBatchImage = (promptIndex: number, imageIndex: number) => {
    const updatedResults = [...batchResults];

    // Check if this image is already selected
    const currentlySelected =
      updatedResults[promptIndex].selectedImageIndex === imageIndex;

    // Update the selected state
    if (currentlySelected) {
      // Deselect if already selected
      updatedResults[promptIndex] = {
        ...updatedResults[promptIndex],
        selectedImageIndex: null,
        images: updatedResults[promptIndex].images.map((img) => ({
          ...img,
          selected: false,
        })),
      };
    } else {
      // Select this image
      updatedResults[promptIndex] = {
        ...updatedResults[promptIndex],
        selectedImageIndex: imageIndex,
        images: updatedResults[promptIndex].images.map((img, idx) => ({
          ...img,
          selected: idx === imageIndex,
        })),
      };
    }

    setBatchResults(updatedResults);
  };

  const runObjectDetection = async () => {
    // Find all selected images
    const selectedPrompts = batchResults.filter(
      (prompt) => prompt.selectedImageIndex !== null
    );

    if (selectedPrompts.length === 0) {
      setError("Please select at least one image first");
      return;
    }

    setDetectingObjects(true);
    setError("");

    try {
      const totalImages = selectedPrompts.length;
      let processedCount = 0;
      let successCount = 0;
      let allDetectionResults: DetectionResult[] = [];

      // Process each selected image
      for (const prompt of selectedPrompts) {
        if (prompt.selectedImageIndex === null) continue;

        try {
          // Get the selected image data
          const selectedImage = prompt.images[prompt.selectedImageIndex];
          const localPath = selectedImage.localPath;

          // Use local path if available, otherwise use base64 data
          let imageUrl = "";

          if (localPath) {
            // If we have a local path, use it directly
            imageUrl = localPath;
            console.log(`Using local path for object detection: ${localPath}`);
          } else {
            // Convert base64 image to file and upload
            const imageFile = await base64ToFile(
              selectedImage.data,
              `selected_image_${processedCount}.png`
            );

            // Create form data for image upload
            const formData = new FormData();
            formData.append("file", imageFile);
            formData.append("image_type", "character");

            console.log(
              `Processing image ${
                processedCount + 1
              }/${totalImages}: Uploading to R2...`
            );

            // Upload to our backend using the character upload endpoint
            const uploadResponse = await api.post(
              "/api/characters/upload-avatar-image",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            if (!uploadResponse.data || !uploadResponse.data.file_url) {
              throw new Error("Upload failed - no file URL returned");
            }

            // Get the file URL from the response
            imageUrl = uploadResponse.data.file_url;
            console.log(
              `Image ${
                processedCount + 1
              }/${totalImages}: Successfully uploaded to ${imageUrl}`
            );
          }

          // Check if this is a talking video prompt
          let isTalkingVideo = false;
          if (
            !prompt.prompt.includes("One hand holding") &&
            !prompt.prompt.includes("A ") &&
            !prompt.prompt.includes(" table")
          ) {
            isTalkingVideo = true;
          } else {
            isTalkingVideo = false;
          }
          console.log("isTalkingVideo", isTalkingVideo);

          let bbox: BoundingBox = {
            x1: 0,
            y1: 0,
            x2: 100,
            y2: 100,
            width: 100,
            height: 100,
          };

          let className = "default_box";
          let score = 1.0;
          let detectionResults: DetectionResult[] = [];

          // Only run object detection if it's not a talking video
          if (!isTalkingVideo) {
            console.log(
              `Image ${
                processedCount + 1
              }/${totalImages}: Running object detection...`
            );

            // Call our backend object detection API with the image URL
            const detectionResponse = await api.post<ObjectDetectionResponse>(
              "/api/characters/object-detection",
              {
                image_url: imageUrl,
              }
            );

            if (!detectionResponse.data || !detectionResponse.data.success) {
              throw new Error(
                detectionResponse.data?.error || "Object detection failed"
              );
            }

            // Get the predictions from the response
            detectionResults = detectionResponse.data.predictions;

            // Get the uploaded image URL if available (from the new Roboflow implementation)
            const uploadedImageUrl = detectionResponse.data.uploaded_image_url;
            if (uploadedImageUrl) {
              imageUrl = uploadedImageUrl;
            }

            console.log(
              `Image ${processedCount + 1}/${totalImages}: Detection results:`,
              detectionResults
            );

            // Add detection results to the aggregated list
            allDetectionResults = [...allDetectionResults, ...detectionResults];

            // If we have valid detection results, use them
            if (detectionResults.length > 0) {
              // Find the product in the detections
              // First try exact match on "product", then try case-insensitive match,
              // then fallback to the first detection if nothing else is found
              const productDetection =
                detectionResults.find((d) => d.class_name === "product") ||
                detectionResults.find(
                  (d) => d.class_name.toLowerCase() === "product"
                ) ||
                detectionResults[0];

              if (productDetection) {
                // Use the detected bounding box
                bbox = productDetection.bbox;
                className = productDetection.class_name;
                score = productDetection.score;

                console.log(
                  `Image ${
                    processedCount + 1
                  }/${totalImages}: Using detection with class "${
                    productDetection.class_name
                  }" at coordinates:`,
                  bbox
                );
              } else {
                console.log(
                  `Image ${
                    processedCount + 1
                  }/${totalImages}: No suitable detection found, using default 100x100 box`
                );
              }
            } else {
              console.log(
                `Image ${
                  processedCount + 1
                }/${totalImages}: No objects detected, using default 100x100 box`
              );
            }
          } else {
            console.log(
              `Image ${
                processedCount + 1
              }/${totalImages}: Skipping object detection for TALKING VIDEO`
            );
          }

          // Create avatar with the extracted or default coordinates
          console.log(
            `Image ${processedCount + 1}/${totalImages}: Creating avatar...`
          );

          let item_type = "product";
          if (isTalkingVideo) {
            item_type = "talking";
          }
          console.log(prompt.prompt);
          console.log(item_type);
          const avatarResponse = await api.post("/api/characters", {
            name:
              detectionResults.length > 0
                ? `${className} ${Math.round(score * 100)}%`
                : `Default Box ${processedCount + 1}`,
            prompt: prompt.prompt,
            values: {}, // Empty values/tags as requested
            character_image_url: imageUrl,
            mask_image_url: "", // Blank as requested
            x: bbox.x1,
            y: bbox.y1,
            width: bbox.width,
            height: bbox.height,
            item_type: item_type,
          });

          console.log(
            `Image ${
              processedCount + 1
            }/${totalImages}: Avatar created successfully`,
            avatarResponse.data
          );
          successCount++;
        } catch (err) {
          console.error(
            `Error processing image ${processedCount + 1}/${totalImages}:`,
            err
          );
        }

        processedCount++;
      }

      // Set detection results for display
      if (allDetectionResults.length > 0) {
        // Find the first product or use the first detection
        const productDetection =
          allDetectionResults.find((d) => d.class_name === "product") ||
          allDetectionResults.find((d) =>
            d.class_name.toLowerCase().includes("product")
          ) ||
          allDetectionResults[0];

        if (productDetection) {
          setDetectionResults([productDetection]);

          // Show available properties based on actual data
          const propertyKeys = Object.keys(productDetection);
          const bboxKeys = Object.keys(productDetection.bbox);

          setDetectionProperties([
            ...propertyKeys.filter((k) => k !== "bbox"),
            ...bboxKeys.map((k) => `bbox.${k}`),
          ]);
        }
      } else {
        // Add a mock detection result to display the default box properties
        const defaultBox: DetectionResult = {
          id: "default",
          class_name: "default_box",
          score: 1.0,
          bbox: {
            x1: 0,
            y1: 0,
            x2: 100,
            y2: 100,
            width: 100,
            height: 100,
          },
        };
        setDetectionResults([defaultBox]);

        // Show available properties for the default box
        const propertyKeys = Object.keys(defaultBox);
        const bboxKeys = Object.keys(defaultBox.bbox);

        setDetectionProperties([
          ...propertyKeys.filter((k) => k !== "bbox"),
          ...bboxKeys.map((k) => `bbox.${k}`),
        ]);
      }

      // Show success message
      if (successCount > 0) {
        setError(
          `Success! Created ${successCount} avatar${
            successCount !== 1 ? "s" : ""
          }${
            allDetectionResults.length === 0
              ? " with default 100x100 box"
              : " with detected product coordinates"
          }.`
        );
      } else {
        setError(
          "Processing completed, but no avatars were created. Please try again."
        );
      }
    } catch (err) {
      console.error("Error in object detection processing:", err);
      setError(err instanceof Error ? err.message : "Object detection failed");
    } finally {
      setDetectingObjects(false);
    }
  };

  // Fetch dropdown options on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/characters/character-options`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Fetched dropdown options:", data.options);
        setDropdownOptions(data.options);
      } catch (err) {
        console.error("Failed to fetch dropdown options:", err);
        setError("Failed to load dropdown options. Please refresh the page.");
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // For debugging - log dropdown options
  useEffect(() => {
    if (Object.keys(dropdownOptions).length > 0) {
      console.log("Current dropdown options:", dropdownOptions);
      console.log("Current selected values:", values);
    }
  }, [dropdownOptions, values]);

  // Progress bar simulation for image generation
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (generatingImages && generateProgress < 95) {
      interval = setInterval(() => {
        setGenerateProgress((prev) => {
          // Move faster at beginning, slower near end
          const increment = prev < 30 ? 5 : prev < 60 ? 3 : prev < 85 ? 1 : 0.5;
          return Math.min(95, prev + increment);
        });
      }, 300);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [generatingImages, generateProgress]);

  const generateCharacter = async () => {
    try {
      setLoading(true);
      setError("");

      // If values haven't been modified or this is the first generation,
      // don't include any selected_values to get a completely fresh character
      const requestPayload = valuesModified ? { selected_values: values } : {};

      const response = await fetch(`${apiUrl}/api/generate-character`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to generate character");
      }

      const data = await response.json();
      setPrompt(data.prompt);
      setValues(data.values || {});

      // Store the new values as original values
      originalValuesRef.current = { ...data.values };

      // Reset the modified state
      setValuesModified(false);

      // Reset any previously generated images
      setGeneratedImages([]);
      setSelectedImageIndex(null);
      setSelectedImageUrl(null);
      // Clear previous mask and regions
      setMaskUrl(null);
      setUploadedRegions([]);
    } catch (err) {
      console.error("Error generating character:", err);
      setError("Failed to generate character");
    } finally {
      setLoading(false);
    }
  };

  const generateImages = async () => {
    if (!prompt) {
      setError("Please generate a prompt first");
      return;
    }

    setGeneratingImages(true);
    setGenerateProgress(0);
    setError("");

    // Maximum number of retries
    const MAX_RETRIES = 5;
    // Initial delay in ms (500ms = 0.5 seconds)
    const INITIAL_DELAY = 500;

    const attemptGeneration = async (retryCount = 0) => {
      try {
        const response = await fetch(
          `${apiUrl}/api/characters/generate-images`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              prompt: prompt,
            }),
          }
        );

        if (response.status === 429) {
          // Too Many Requests
          // If we haven't exhausted retries
          if (retryCount < MAX_RETRIES) {
            // Calculate exponential backoff with jitter
            // Exponential backoff: delay = initialDelay * 2^retryCount
            // Adding jitter: random amount between 0 and 1 second
            const delay =
              INITIAL_DELAY * Math.pow(2, retryCount) +
              Math.floor(Math.random() * 1000);

            console.log(
              `Rate limited. Retrying in ${delay}ms (attempt ${retryCount + 1})`
            );

            // Update progress to show we're waiting
            setGenerateProgress((prev) => Math.min(85, prev + 1));

            // Wait and retry
            await new Promise((resolve) => setTimeout(resolve, delay));
            return attemptGeneration(retryCount + 1);
          } else {
            throw new Error(
              "Rate limit exceeded. Please try again in a moment."
            );
          }
        }

        if (!response.ok) {
          throw new Error(`Failed to generate images: ${response.statusText}`);
        }

        const data = await response.json();

        // Ensure completion to 100%
        setGenerateProgress(100);

        // Store local paths
        const localPaths = data.local_paths || [];

        // Convert the base64 strings to GeneratedImage objects
        const images: GeneratedImage[] = data.images.map(
          (base64String: string, index: number) => ({
            data: `data:image/png;base64,${base64String}`,
            selected: false,
            localPath: localPaths[index] || null,
          })
        );

        setGeneratedImages(images);
        setCreditsLeft(data.credits_left);

        return true;
      } catch (err: any) {
        // If this is a network error and we haven't exhausted retries
        if (
          (err.name === "TypeError" || err.message.includes("network")) &&
          retryCount < MAX_RETRIES
        ) {
          const delay =
            INITIAL_DELAY * Math.pow(2, retryCount) +
            Math.floor(Math.random() * 1000);

          console.log(
            `Network issue. Retrying in ${delay}ms (attempt ${retryCount + 1})`
          );

          // Update progress to show we're waiting
          setGenerateProgress((prev) => Math.min(85, prev + 2));

          // Wait and retry
          await new Promise((resolve) => setTimeout(resolve, delay));
          return attemptGeneration(retryCount + 1);
        }

        throw err;
      }
    };

    try {
      await attemptGeneration();
    } catch (err) {
      console.error("Error generating images:", err);
      setError("Failed to generate images. Please try again.");
      setGenerateProgress(0);
    } finally {
      // Small delay to ensure progress bar finishes animation
      setTimeout(() => {
        setGeneratingImages(false);
      }, 500);
    }
  };

  const selectImage = async (index: number) => {
    try {
      setSelectedImageIndex(index);
      const selectedImage = generatedImages[index];
      const selectedImageData = selectedImage.data;
      const localPath = selectedImage.localPath;

      // Convert base64 image to file
      const imageFile = await base64ToFile(
        selectedImageData,
        `character_${index}.png`
      );

      // Create form data for image upload
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("image_type", "character");

      // Upload to our backend using the character upload endpoint
      const uploadResponse = await api.post(
        "/api/characters/upload-avatar-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!uploadResponse.data || !uploadResponse.data.file_url) {
        throw new Error("Upload failed - no file URL returned");
      }

      // Get the file URL from the response
      const imageUrl = uploadResponse.data.file_url;

      // Update the selected image URL and local path
      setSelectedImageUrl(imageUrl);

      // Store the local path if available for object detection
      if (localPath) {
        console.log(`Selected image has local path: ${localPath}`);
      }

      // Reset mask and regions when a new image is selected
      setMaskUrl(null);
      setUploadedRegions([]);

      // Reset the mask selector component if available
      if (maskSelectorRef.current) {
        maskSelectorRef.current.resetComponentState();
      }

      // Update the selected state of each image
      setGeneratedImages(
        generatedImages.map((img, i) => ({
          ...img,
          selected: i === index,
        }))
      );

      // Show success message
      setError("Image uploaded successfully!");
    } catch (err: any) {
      console.error("Error uploading selected image:", err);
      setError(err.message || "Failed to upload selected image");
    }
  };

  const handleValueChange = async (category: string, newValue: string) => {
    // Update the values object with the new value
    const updatedValues = { ...values, [category]: newValue };

    // Mark that values have been modified
    setValuesModified(true);

    // Generate a new prompt with the updated values
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${apiUrl}/api/generate-character`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          selected_values: updatedValues,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update character");
      }

      const data = await response.json();
      setPrompt(data.prompt);
      setValues(data.values || {});

      // Reset any previously generated images
      setGeneratedImages([]);
      setSelectedImageIndex(null);
      setSelectedImageUrl(null);
      // Clear previous mask and regions to prevent using old data
      setMaskUrl(null);
      setUploadedRegions([]);

      // Reset the mask selector component if available
      if (maskSelectorRef.current) {
        maskSelectorRef.current.resetComponentState();
      }
    } catch (err) {
      console.error("Error updating character:", err);
      setError("Failed to update character. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Update valuesByCategory when values or dropdownOptions change
  useEffect(() => {
    if (!Object.keys(values).length || !Object.keys(dropdownOptions).length)
      return;

    const newValuesByCategory: { [category: string]: string } = {};

    Object.entries(values).forEach(([category, value]) => {
      const categoryValue = getCategoryForValue(value);
      if (categoryValue) {
        newValuesByCategory[category] = categoryValue;
      }
    });

    setValuesByCategory(newValuesByCategory);
    console.log("Updated values by category:", newValuesByCategory);
  }, [values, dropdownOptions]);

  // Determine which category a value belongs to
  const getCategoryForValue = (value: string): string => {
    if (!value || !Object.keys(dropdownOptions).length) return "";

    // First check if the value is a key in any category
    for (const [category, options] of Object.entries(dropdownOptions)) {
      if (options && options.includes(value)) {
        return category;
      }
    }

    // If not found, check if it's a value in any category
    for (const [category, options] of Object.entries(dropdownOptions)) {
      if (options) {
        const valueValues = Object.values(options);
        for (const valueValue of valueValues) {
          if (typeof valueValue === "string" && valueValue === value) {
            return category;
          }
        }
      }
    }

    return "";
  };

  // Render a dropdown with option groups if needed
  const renderDropdown = (category: string, label: string) => {
    // Check if the category exists in our options
    if (!dropdownOptions || !dropdownOptions[category]) {
      return <div>Loading options...</div>;
    }

    const options = organizeDropdownOptions(dropdownOptions, category);
    const currentValue = values[category] || "";

    // If this category has grouped options (like background)
    if (options.length > 0 && isOptionGroup(options[0])) {
      return (
        <select
          value={currentValue}
          onChange={(e) => handleValueChange(category, e.target.value)}
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="" disabled>
            Select {label}
          </option>

          {options.map((group, groupIndex) => {
            if (isOptionGroup(group)) {
              return (
                <optgroup key={groupIndex} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              );
            }
            return null;
          })}
        </select>
      );
    }

    // For categories without groups - fix the OptionItem mapping
    return (
      <select
        value={currentValue}
        onChange={(e) => handleValueChange(category, e.target.value)}
        disabled={loading}
        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="" disabled>
          Select {label}
        </option>

        {options.map((option) => {
          // Only render simple options, not option groups
          if (!isOptionGroup(option)) {
            return (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            );
          }
          return null;
        })}
      </select>
    );
  };

  // Render the progress bar for image generation
  const renderProgressBar = () => {
    return (
      <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
        <div
          className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${generateProgress}%` }}
        ></div>
      </div>
    );
  };

  // Handler for name input
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Form validation checks
    if (submitting) {
      console.log(
        "Submission already in progress, preventing duplicate submission"
      );
      return;
    }

    if (uploadedRegions.length < 1) {
      setError("Please create a selection region first");
      return;
    }

    if (!uploadedRegions[0].coordinates) {
      setError("Invalid selection coordinates");
      return;
    }

    if (!selectedImageUrl) {
      setError("No character image selected. Please select an image first.");
      return;
    }

    if (!maskUrl) {
      setError("No mask created. Please create and save a mask first.");
      return;
    }

    if (!prompt || Object.keys(values).length === 0) {
      setError("Missing character prompt or values");
      return;
    }

    console.log("Starting character submission with:");
    console.log("- Image URL:", selectedImageUrl);
    console.log("- Mask URL:", maskUrl);
    console.log("- Coordinates:", uploadedRegions[0].coordinates);
    console.log("- Values:", values);

    setSubmitting(true);
    setError("");

    try {
      // Verify coordinates are valid numbers
      const coords = uploadedRegions[0].coordinates;
      if (
        isNaN(coords.x) ||
        isNaN(coords.y) ||
        isNaN(coords.width) ||
        isNaN(coords.height) ||
        coords.width <= 0 ||
        coords.height <= 0
      ) {
        throw new Error("Invalid selection coordinates");
      }

      // Send API request to create the character
      const uploadResponse = await api.post("/api/characters", {
        prompt: prompt,
        values: values,
        character_image_url: selectedImageUrl,
        mask_image_url: maskUrl,
        x: coords.x,
        y: coords.y,
        width: coords.width,
        height: coords.height,
      });

      console.log("Character creation successful:", uploadResponse.data);

      // Show success message
      setError(
        "Character created successfully! You can create another character with the same attributes."
      );

      // Reset mask and regions for the next character
      setMaskUrl(null);
      setUploadedRegions([]);

      // Reset the mask selector component
      if (maskSelectorRef.current) {
        maskSelectorRef.current.resetComponentState();
      }

      // Allow user to create a new character variation by keeping the current prompt and values
      // but clearing the selected image and mask
      setSelectedImageIndex(null);
      setSelectedImageUrl(null);

      // Clear generated images to allow for fresh generation next time
      setGeneratedImages([]);

      // Keep the values and prompt intact for easy creation of variations
      // No need to call generateImages() here as we want the user to explicitly request new images
    } catch (err) {
      console.error("Error creating character:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle debug image upload
  const handleDebugImageSelected = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setDebugImage(file);
      const fileUrl = URL.createObjectURL(file);
      setDebugImageUrl(fileUrl);
    }
  };

  // Handle rectangle coordinate changes
  const handleRectCoordinateChange = (
    key: keyof RectangleCoordinates,
    value: number
  ) => {
    setRectCoordinates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Cleanup any created object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (debugImageUrl) {
        URL.revokeObjectURL(debugImageUrl);
      }
    };
  }, [debugImageUrl]);

  // Add this new function to handle avatar creation
  const handleCreateAvatar = async (
    maskUrl: string | undefined,
    coordinates: RectangleCoordinates
  ) => {
    try {
      setSubmitting(true);
      setError("");

      if (!selectedImageUrl || !maskUrl) {
        setError("Missing image or mask URL");
        return;
      }

      // Convert base64 images to files
      const characterFile = await base64ToFile(
        selectedImageUrl,
        "character.png"
      );
      const maskFile = await base64ToFile(maskUrl, "mask.png");

      // Upload both images to R2
      const [characterUpload, maskUpload] = await Promise.all([
        uploadAvatarImage(characterFile, "character"),
        uploadAvatarImage(maskFile, "mask"),
      ]);

      if (!characterUpload.success || !maskUpload.success) {
        throw new Error("Failed to upload images");
      }

      // Create avatar with the uploaded URLs
      const response = await api.post("/api/avatars", {
        name: "My Avatar", // You might want to add a name input field
        prompt: prompt,
        values: values,
        character_image_url: characterUpload.url,
        mask_image_url: maskUpload.url,
        x: coordinates.x,
        y: coordinates.y,
        width: coordinates.width,
        height: coordinates.height,
      });

      if (response.data.success) {
        // Reset form and redirect
        setSelectedImageIndex(null);
        setSelectedImageUrl(null);
        setGeneratedImages([]);
        navigateTo("/");
      }
    } catch (err: any) {
      console.error("Error creating avatar:", err);
      setError(err.message || "Failed to create avatar");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to convert base64 to file
  const base64ToFile = async (
    base64String: string,
    fileName: string
  ): Promise<File> => {
    // Remove data URL prefix if present
    const base64Data = base64String.includes("base64,")
      ? base64String.split("base64,")[1]
      : base64String;

    // Convert base64 to blob
    const response = await fetch(`data:image/png;base64,${base64Data}`);
    const blob = await response.blob();

    return new File([blob], fileName, { type: "image/png" });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Create New Character</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}

      {/* Batch processing section */}
      <div className="bg-gray-50 p-6 rounded-lg mb-8 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">Batch Process Prompts</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter prompts (one per line)
          </label>
          <textarea
            value={batchPrompts}
            onChange={handleBatchPromptChange}
            rows={5}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter multiple prompts, one per line..."
            disabled={batchProcessing}
          />
        </div>

        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={generateBatchImages}
            disabled={batchProcessing || !batchPrompts.trim()}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {batchProcessing ? "Generating..." : "Generate Batch Images"}
          </button>

          {creditsLeft !== null && (
            <div className="text-sm text-gray-600">
              Credits remaining: {creditsLeft}
            </div>
          )}
        </div>

        {/* Progress bar for batch processing */}
        {batchProcessing && (
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${batchProgress}%` }}
            ></div>
          </div>
        )}

        {/* Batch results */}
        {batchResults.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-4">Generated Images</h3>

            {batchResults.map((result, promptIndex) => (
              <div
                key={promptIndex}
                className="mb-8 pb-6 border-b border-gray-200"
              >
                <h4 className="font-medium mb-2">Prompt {promptIndex + 1}:</h4>
                <p className="text-gray-700 mb-4 bg-gray-100 p-3 rounded">
                  {result.prompt}
                </p>

                {result.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {result.images.map((image, imageIndex) => (
                      <div
                        key={imageIndex}
                        className={`relative cursor-pointer border-4 rounded-lg transition-all ${
                          image.selected
                            ? "border-blue-500 scale-105"
                            : "border-transparent hover:border-blue-300"
                        }`}
                        onClick={() =>
                          selectBatchImage(promptIndex, imageIndex)
                        }
                      >
                        <img
                          src={image.data}
                          alt={`Generated for prompt ${
                            promptIndex + 1
                          }, image ${imageIndex + 1}`}
                          className="w-full h-auto rounded-lg shadow-md"
                        />
                        <div className="absolute top-2 right-2 bg-white bg-opacity-75 rounded-full w-6 h-6 flex items-center justify-center">
                          {imageIndex + 1}
                        </div>
                        {image.selected && (
                          <div className="absolute top-2 left-2 bg-blue-500 text-white rounded-full px-2 py-1 text-xs">
                            Selected
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    No images generated for this prompt yet
                  </p>
                )}
              </div>
            ))}

            {/* Object detection section */}
            <div className="mt-6">
              <button
                onClick={runObjectDetection}
                disabled={
                  detectingObjects ||
                  !batchResults.some((r) => r.selectedImageIndex !== null)
                }
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                {detectingObjects
                  ? "Detecting Objects..."
                  : "Run Object Detection on Selected Image"}
              </button>

              {detectionResults.length > 0 && (
                <div className="mt-4 bg-gray-100 p-4 rounded">
                  <h4 className="font-medium mb-2">Detection Results:</h4>
                  <pre className="bg-white p-3 rounded overflow-auto text-sm">
                    {JSON.stringify(detectionResults, null, 2)}
                  </pre>

                  <h4 className="font-medium mt-4 mb-2">
                    Available Properties:
                  </h4>
                  <pre className="bg-white p-3 rounded overflow-auto text-sm">
                    {JSON.stringify(detectionProperties, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Original form */}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <button
            onClick={generateCharacter}
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {loading
              ? "Generating..."
              : valuesModified
              ? "Regenerate with Selected Options"
              : "Generate Random Character"}
          </button>

          {valuesModified && Object.keys(values).length > 0 && (
            <button
              onClick={() => {
                setValuesModified(false);
                generateCharacter();
              }}
              disabled={loading}
              className="ml-2 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            >
              Generate Completely Random
            </button>
          )}
        </div>

        {Object.keys(values).length > 0 &&
          dropdownOptions &&
          !optionsLoading && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold mb-4">
                Customize Character:
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* Background gets its own full-width section */}
                <div className="lg:col-span-2 mb-4 p-3 bg-white rounded-md shadow-sm">
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Background Setting
                  </label>
                  {renderDropdown("background", "Background")}
                </div>

                {/* Other options in a 2-column grid */}
                {Object.entries(dropdownOptions)
                  .filter(([category]) => category !== "background")
                  .map(([category, options]) => (
                    <div
                      key={category}
                      className="mb-4 p-3 bg-white rounded-md shadow-sm"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {category.charAt(0).toUpperCase() +
                          category.slice(1).replace("_", " ")}
                      </label>
                      {renderDropdown(category, category)}
                    </div>
                  ))}
              </div>
            </div>
          )}

        {Object.keys(values).length > 0 && (
          <div className="mb-4">
            <h2 className="text-xl font-semibold mb-2">Selected Values:</h2>
            <div className="bg-gray-100 p-4 rounded-lg">
              {Object.entries(values).map(([category, value]) => (
                <div key={category} className="mb-2">
                  <span className="font-semibold">
                    {category.charAt(0).toUpperCase() +
                      category.slice(1).replace("_", " ")}
                    :
                  </span>{" "}
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {prompt && (
          <div className="mt-4">
            <h2 className="text-xl font-semibold mb-2">Character Prompt:</h2>
            <div className="bg-gray-100 p-4 rounded-lg whitespace-pre-wrap mb-4">
              {prompt}
            </div>

            {/* Image generation button */}
            <button
              onClick={generateImages}
              disabled={generatingImages || loading}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 mb-4"
            >
              {generatingImages ? "Generating Images..." : "Generate Images"}
            </button>

            {creditsLeft !== null && (
              <div className="text-sm text-gray-600 mb-4">
                Credits remaining: {creditsLeft}
              </div>
            )}

            {/* Progress bar */}
            {generatingImages && renderProgressBar()}

            {/* Generated images */}
            {generatedImages.length > 0 && (
              <div className="mt-4">
                <h2 className="text-xl font-semibold mb-2">
                  {selectedImageIndex !== null
                    ? "Selected Image:"
                    : "Generated Images (Select One):"}
                </h2>

                {/* Show all images if none selected, otherwise show only the selected one */}
                <div
                  className={`grid ${
                    selectedImageIndex === null
                      ? "grid-cols-2 md:grid-cols-4"
                      : "grid-cols-1 md:grid-cols-1"
                  } gap-4`}
                >
                  {selectedImageIndex !== null ? (
                    <div className="relative">
                      <img
                        src={generatedImages[selectedImageIndex].data}
                        alt={`Generated character ${selectedImageIndex + 1}`}
                        className="w-full h-auto rounded-lg shadow-md"
                      />
                      <button
                        onClick={() => setSelectedImageIndex(null)}
                        className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
                      >
                        View All Images
                      </button>
                    </div>
                  ) : (
                    generatedImages.map((image, index) => (
                      <div
                        key={index}
                        className={`relative cursor-pointer border-4 rounded-lg transition-all ${
                          image.selected
                            ? "border-blue-500 scale-105"
                            : "border-transparent hover:border-blue-300"
                        }`}
                        onClick={() => selectImage(index)}
                      >
                        <img
                          src={image.data}
                          alt={`Generated character ${index + 1}`}
                          className="w-full h-auto rounded-lg shadow-md"
                        />
                        <div className="absolute top-2 right-2 bg-white bg-opacity-75 rounded-full w-6 h-6 flex items-center justify-center">
                          {index + 1}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Replace the existing Image Mask Selector section with this */}
            {selectedImageIndex !== null && selectedImageUrl && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h2 className="text-xl font-semibold mb-4">
                  Create Image Mask
                </h2>
                <p className="text-gray-600 mb-4">
                  Select the area of the image to create a mask for your avatar.
                </p>

                <ImageMaskSelector
                  imageUrl={selectedImageUrl}
                  onMaskGenerated={async (maskDataUrl, coordinates) => {
                    try {
                      // Convert mask data URL to file
                      const maskFile = await base64ToFile(
                        maskDataUrl,
                        "mask.png"
                      );

                      // Create form data for mask upload
                      const formData = new FormData();
                      formData.append("file", maskFile);
                      formData.append("image_type", "mask");

                      // Upload to our backend using the character upload endpoint
                      const uploadResponse = await api.post(
                        "/api/characters/upload-avatar-image",
                        formData,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );

                      if (
                        !uploadResponse.data ||
                        !uploadResponse.data.file_url
                      ) {
                        throw new Error("Upload failed - no file URL returned");
                      }

                      // Get the file URL from the response
                      const { file_url: maskUrl } = uploadResponse.data;

                      // Store the uploaded URL and coordinates for later use
                      setUploadedRegions([
                        {
                          coordinates: coordinates,
                          file: maskFile,
                        },
                      ]);

                      // Set the mask URL
                      setMaskUrl(maskUrl);

                      // Show success message
                      setError(
                        "Mask uploaded successfully! You can now create your avatar."
                      );
                    } catch (err: any) {
                      console.error("Error uploading mask:", err);
                      setError(err.message || "Failed to upload mask");
                    }
                  }}
                  onRegionsUpdated={handleRegionsUpdated}
                />
              </div>
            )}

            {uploadedRegions.length > 0 && (
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <h3 className="text-lg font-medium mb-2 text-orange-800">
                  Uploaded Regions
                </h3>
                <p className="text-sm mb-3">
                  The following regions will be included with your character:
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-orange-100">
                        <th className="py-2 px-4 text-left">Region</th>
                        <th className="py-2 px-4 text-left">Coordinates</th>
                        <th className="py-2 px-4 text-left">File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedRegions.map((region, index) => (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? "bg-orange-50" : "bg-white"
                          }
                        >
                          <td className="py-2 px-4">Region {index + 1}</td>
                          <td className="py-2 px-4">
                            x:{region.coordinates.x}, y:{region.coordinates.y},
                            w:
                            {region.coordinates.width}, h:
                            {region.coordinates.height}
                          </td>
                          <td className="py-2 px-4">
                            {region.file ? (
                              <span className="text-green-600">
                                ✓ {region.file.name}
                              </span>
                            ) : (
                              <span className="text-red-600">
                                No file selected
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <button
            type="submit"
            disabled={submitting || (!prompt && !selectedImageIndex)}
            className={`px-6 py-3 rounded-md font-medium ${
              submitting || (!prompt && !selectedImageIndex)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {submitting ? "Saving..." : "Save Character"}
          </button>
        </div>
      </form>
    </div>
  );
}
